/**
 * @description Account activity and statistics service.
 */

import api from "@/utils/axios";
import type {
  AccountActivity,
  AccountStatistics,
  AccountStatisticsDto,
  ActivityTimePeriod,
  WebsiteActionType,
} from "@/types/activity.types";
import { DEFAULT_ACTION_COUNTS } from "@/types/activity.types";
import type { PagedResponse } from "@/types/pagination.types";

const normalizeStatistics = (
  payload: AccountStatisticsDto
): AccountStatistics => ({
  memberSince: payload.memberSince,
  activityCount: payload.activityCount,
  actionCounts: {
    ...DEFAULT_ACTION_COUNTS,
    ...(payload.actionCounts ?? {}),
  },
});

export interface AccountActivityQuery {
  page?: number;
  size?: number;
  search?: string;
  actionType?: WebsiteActionType;
  timePeriod?: ActivityTimePeriod;
}

const buildActivityParams = (query?: AccountActivityQuery) => {
  const params = new URLSearchParams();
  if (!query) return params;
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.size !== undefined) params.set("size", String(query.size));
  if (query.search) params.set("search", query.search);
  if (query.actionType) params.set("actionType", query.actionType);
  if (query.timePeriod) params.set("timePeriod", query.timePeriod);
  return params;
};

export const accountService = {
  async getActivity(
    query?: AccountActivityQuery
  ): Promise<PagedResponse<AccountActivity>> {
    const params = buildActivityParams(query);
    const queryString = params.toString();
    const url = `/account/activity${queryString ? `?${queryString}` : ""}`;
    const response = await api.get<PagedResponse<AccountActivity>>(url);
    return response.data;
  },

  async getStatistics(): Promise<AccountStatistics> {
    const response = await api.get<AccountStatisticsDto>("/account/statistics");
    return normalizeStatistics(response.data);
  },
};
